import { createElement as _createElement } from "react";
import { jsx as _jsx } from "react/jsx-runtime";
import { Autocomplete, TextField as MuiTextField, createFilterOptions } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useController } from 'react-hook-form';
import { Chip } from '../Chip';
import { CheckIconContainer, OptionAvatar, OptionCheckbox, OptionListItem } from './CheckboxAutocomplete.styled';
import { t } from '@/core/localization';
import { NoOptionsTypography } from '../shared.styled';
import { Skeleton } from '../Skeleton';
import { CheckIcon } from './CheckIcon';
import { SelectAllPaper } from './SelectAllPaper';
/**
 * Autocomplete with checkboxes and one SelectAll checkbox that checks or unchecks only filtered options.
 * @param param0
 */
const CheckboxAutocomplete = ({ options, keyField, labelField, iconField, chipColor = 'primary', limitTags, checkboxStyle = 'default', ...controllerProps }) => {
    // Used so store input value after option is selected (Mui resets input value by default). Required for filter and 'select all'.
    const [inputValue, setInputValue] = useState('');
    const [filteredOptionIds, setFilteredOptionIds] = useState([]);
    const { field: { value, onChange, ...field }, fieldState: { error }, } = useController(controllerProps);
    const allFilteredItemsAreChecked = useMemo(() => filteredOptionIds.every((item) => value.includes(item)), [filteredOptionIds, value]);
    const allFilteredItemsAreUnChecked = useMemo(() => filteredOptionIds.every((item) => !value.includes(item)), [filteredOptionIds, value]);
    const handleSelectAllCheckboxClick = useCallback((event) => {
        // prevents blur
        event.preventDefault();
        if (allFilteredItemsAreChecked) {
            onChange(value.filter((item) => !filteredOptionIds.some((filteredItem) => filteredItem === item)));
        }
        else {
            const itemsToAdd = filteredOptionIds.filter((filteredItem) => !(value ?? []).some((item) => item === filteredItem));
            onChange([...(value ?? []), ...itemsToAdd]);
        }
    }, [allFilteredItemsAreChecked, filteredOptionIds, onChange, value]);
    // TODO: check later if it works without useMemo, since it is MUI5 bug: https://github.com/mui/material-ui/issues/31073.
    const selectAllPaper = useMemo(() => (paperProps) => {
        return (_jsx(SelectAllPaper, { paperProps: paperProps, 
            // checkboxColor={chipColor}
            selectAllCheckboxChecked: allFilteredItemsAreChecked, selectAllCheckboxIndeterminate: !allFilteredItemsAreChecked && !allFilteredItemsAreUnChecked, nothingFound: !filteredOptionIds.length, onSelectAllCheckboxClick: handleSelectAllCheckboxClick }));
    }, [allFilteredItemsAreChecked, allFilteredItemsAreUnChecked, filteredOptionIds.length, handleSelectAllCheckboxClick]);
    // useMemo inside MuiAutocomplete recommended by MUI when value is controlled.
    const selectedValues = useMemo(() => (value && options ? options.filter((option) => value.includes(option[keyField])) : []), [keyField, options, value]);
    const getFilteredOptions = createFilterOptions();
    // Getting actual filtered options' ids.
    const filterOptions = useCallback((options, state) => {
        // Because of using useState here we have the following console warning:
        // Warning: Cannot update a component (`CheckboxAutocomplete`) while rendering a different
        // component (`ForwardRef(Autocomplete)`).
        // To locate the bad setState() call inside `ForwardRef(Autocomplete)`, follow the stack trace as described
        // in https://reactjs.org/link/setstate-in-render Error Component Stack
        const filteredOptions = getFilteredOptions(options, state);
        // This `if` check is intended to prevent infinite rerenders.
        const filteredIds = filteredOptions.map((option) => option[keyField]);
        if (filteredOptionIds.length !== filteredIds.length) {
            setFilteredOptionIds(filteredIds);
        }
        return filteredOptions;
    }, [getFilteredOptions, filteredOptionIds, keyField]);
    // TODO: later. Not working for some reason.
    // const renderValue: AutocompleteProps<TOption[], true, false, false>['renderValue'] = (value, getValueProps, ownerState) => {
    // 	return value?.map((option, index: number) => {
    // 		// Key value should be passed within `key` prop only. When it used through spread operator it leads to react console error.
    // 		const { key, ...valueProps } = getValueProps({ index });
    // 		return <Chip key={key} variant="outlined" size="small" color={chipColor} label={option[labelField] as string} {...valueProps} />;
    // 	});
    // };
    if (!options)
        return (_jsx(Skeleton, { children: _jsx(MuiTextField, { size: "small" }) }));
    return (_jsx(Autocomplete
    // open={true} // Uncomment to debug custom paper styles.
    , { 
        // open={true} // Uncomment to debug custom paper styles.
        multiple: true, disableCloseOnSelect: true, ...field, limitTags: limitTags, value: selectedValues, onChange: (event, newValue) => {
            const selectedIds = newValue.map((item) => item[keyField]);
            onChange(selectedIds);
        }, options: options ?? [], getOptionLabel: (option) => option[labelField], defaultValue: [], noOptionsText: _jsx(NoOptionsTypography, { children: t('nothing.is.found.label') }), renderInput: (params) => _jsx(MuiTextField, { ...params, size: "small", error: Boolean(error), label: "", placeholder: "" }), renderValue: (value, getValueProps) => {
            return value?.map((option, index) => {
                // Key value should be passed within `key` prop only. When it used through spread operator it leads to react
                // console error.
                const { key, ...valueProps } = getValueProps({ index });
                return (_jsx(Chip, { variant: "outlined", size: "small", color: chipColor, label: option[labelField], ...valueProps }, key));
            });
        }, renderOption: (props, option, { selected }) => (_createElement(OptionListItem, { ...props, key: option[keyField] },
            checkboxStyle === 'default' && _jsx(OptionCheckbox, { checked: selected }),
            iconField && _jsx(OptionAvatar, { alt: option[labelField], src: `data:image/jpeg;base64, ${option[iconField]}` }),
            option[labelField],
            checkboxStyle === 'iconEnd' && selected && (_jsx(CheckIconContainer, { children: _jsx(CheckIcon, {}) })))), onInputChange: (_, value, reason) => reason !== 'reset' && setInputValue(value), inputValue: inputValue, filterOptions: filterOptions, slots: { paper: selectAllPaper } }));
};
export { CheckboxAutocomplete };
